import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { api, errMsg, downloadFile } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth, EDITOR_ROLES, USER_READ_ROLES } from "../context/AuthContext";
import { PageHeader } from "../components/Card";
import { DataTable, FilterSelect, Pager } from "../components/DataTable";
import { Severity, StatusPill } from "../components/Severity";

const FIELDS = [["trigger", "trigger"], ["required_inputs", "inputs"], ["expected_output", "expected"], ["test_type", "testType"],
  ["required_evidence", "evidenceReq"], ["acceptance_criteria", "acceptance"], ["negative_test", "negative"],
  ["control_family", "controlFamily"], ["entity_mapping", "entityMap"]];

const Detail = ({ uc, onClose }) => {
  const { t, tv, dir } = useI18n();
  const { user, hasRole } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  useEffect(() => { if (hasRole(USER_READ_ROLES)) api.get("/users").then((r) => setUsers(r.data)); }, [hasRole]);
  const create = async () => {
    try {
      const { data } = await api.post(`/use-cases/${uc.id}/tasks`, { assignee_id: assignee || user.id, due_date: due || null });
      toast.success(t("taskCreated")); nav(`/tasks/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open={!!uc} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side={dir === "rtl" ? "left" : "right"} className="w-full overflow-y-auto sm:max-w-xl" data-testid="use-case-detail">
        {uc && (<>
          <SheetHeader><SheetTitle className="text-start"><span className="font-mono text-sm text-sky-700">{uc.id}</span><br />{uc.title}</SheetTitle></SheetHeader>
          <div className="mt-4 flex flex-wrap gap-2"><Severity value={uc.priority} /><StatusPill value={uc.sandbox_status} /><StatusPill value={uc.level} /><StatusPill value={uc.industry} /></div>
          <p className="mt-4 text-sm leading-relaxed text-slate-700">{uc.description}</p>
          <div className="mt-4 rounded-xl bg-slate-50 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{t("workflow")}</p>
            <ol className="list-decimal space-y-1 ps-5 text-sm text-slate-700">{uc.workflow.map((w) => <li key={w}>{w}</li>)}</ol>
          </div>
          <dl className="mt-4 space-y-3 text-sm">
            {FIELDS.map(([k, l]) => <div key={k}><dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t(l)}</dt><dd className="mt-0.5 text-slate-800">{tv(uc[k])}</dd></div>)}
            <div><dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("risk")}</dt><dd className="mt-1"><Severity value={uc.risk_level} /></dd></div>
          </dl>
          {hasRole(EDITOR_ROLES) && (
            <div className="mt-6 space-y-3 rounded-xl border border-slate-200 p-4">
              {users.length > 0 && <FilterSelect value={assignee} onChange={setAssignee} options={users.map((u) => ({ value: u.id, label: `${u.name} · ${u.role}` }))} placeholder={t("assignee")} testId="task-assignee-select" allowAll={false} />}
              <Input data-testid="task-due-date-input" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="rounded-xl" aria-label={t("dueDate")} />
              <Button data-testid="create-task-button" onClick={create} className="w-full rounded-xl bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" />{t("createTask")}</Button>
            </div>
          )}
        </>)}
      </SheetContent>
    </Sheet>
  );
};

export default function UseCases() {
  const { t, tv } = useI18n();
  const { isAdmin } = useAuth();
  const [facets, setFacets] = useState(null);
  const [f, setF] = useState({ q: "", domain: "", industry: "", level: "", priority: "", sandbox_status: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [sel, setSel] = useState(null);

  useEffect(() => { api.get("/use-cases/facets").then((r) => setFacets(r.data)); }, []);
  const load = useCallback(() => { api.get("/use-cases", { params: { ...f, page, limit: 25 } }).then((r) => setData(r.data)); }, [f, page]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);
  const setFilter = (k) => (v) => { setPage(1); setF((p) => ({ ...p, [k]: v })); };
  const open = async (r) => setSel((await api.get(`/use-cases/${r.id}`)).data);

  const cols = [
    { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs text-sky-700">{r.id}</span> },
    { key: "title", label: t("title"), render: (r) => <span className="font-medium text-slate-900">{r.title}</span> },
    { key: "domain", label: t("domain"), render: (r) => tv(r.domain) },
    { key: "industry", label: t("industry") },
    { key: "level", label: t("level") },
    { key: "priority", label: t("priority"), render: (r) => <Severity value={r.priority} /> },
    { key: "sandbox_status", label: t("sandboxStatus"), render: (r) => <StatusPill value={r.sandbox_status} /> },
  ];

  return (
    <div>
      <PageHeader title={t("nav_usecases")} sub={`UC-0001 → UC-1000 · ${data.total}`}>
        {isAdmin && <Button data-testid="export-use-cases-button" variant="outline" className="rounded-xl" onClick={() => downloadFile("/export/use-cases.xlsx", "GRC_Task_Manager_1000_Use_Cases.xlsx").catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4" />{t("exportXlsx")}</Button>}
      </PageHeader>
      <div className="mb-4 flex flex-col flex-wrap gap-3 sm:flex-row">
        <div className="relative flex-1 sm:min-w-[240px]">
          <Search className="absolute start-3 top-3 h-4 w-4 text-slate-400" />
          <Input data-testid="use-case-search-input" value={f.q} onChange={(e) => setFilter("q")(e.target.value)} placeholder={t("search")} className="h-10 rounded-xl ps-9" />
        </div>
        {facets && [["domain", "domain"], ["industry", "industry"], ["level", "level"], ["priority", "priority"], ["sandbox_status", "sandboxStatus"]].map(([k, l]) => (
          <FilterSelect key={k} value={f[k]} onChange={setFilter(k)} options={facets[k]} placeholder={t(l)} testId={`filter-${k}`} />
        ))}
      </div>
      <DataTable cols={cols} rows={data.items} testId="use-cases-table" onRowClick={open} />
      <Pager page={page} total={data.total} limit={25} onPage={setPage} />
      <Detail uc={sel} onClose={() => setSel(null)} />
    </div>
  );
}
